import React from "react"
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  useTheme
} from "@mui/material"
import SortableTableHeaderCell from "src/components/table/SortableTableHeaderCell"
import TableHeaderText from "src/components/table/TableHeaderText"
import type { CompanyMembersSortBy, NamedCommunity } from "src/services/api/companyMembers"
import type { CompanyProfile } from "src/services/api/companyMembers"
import type { GatedCommunityProfileStatus } from "src/services/api/gatedCommunities"
import { renderRolesCell } from "./companyMembersUtils"
import { Role } from "src/UserService"
import { useUser } from "src/providers"

export type MemberProfileStatus = CompanyProfile | GatedCommunityProfileStatus

export interface Member<TProfile extends string = MemberProfileStatus> {
  userId: string
  email: string
  firstName?: string
  lastName?: string
  profile: TProfile | null
  roles: Role[]
  communities?: NamedCommunity[]
  isPending?: boolean
}

interface MembersTableProps<TProfile extends string = MemberProfileStatus> {
  members: Member<TProfile>[]
  loading: boolean
  totalCount: number
  page: number
  rowsPerPage: number
  orderBy: CompanyMembersSortBy
  order: "asc" | "desc"
  onRequestSort: (property: CompanyMembersSortBy) => void
  onPageChange: (_event: unknown, newPage: number) => void
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  showGatedCommunitiesColumn: boolean
  renderProfileCell: (member: Member<TProfile>) => React.ReactNode
  renderActions: (member: Member<TProfile>) => React.ReactNode
  renderCommunitiesCell?: (member: Member<TProfile>, login: string) => React.ReactNode
  tableAriaLabel?: string
}

export function MembersTable<TProfile extends string = MemberProfileStatus>({
  members,
  loading,
  totalCount,
  page,
  rowsPerPage,
  orderBy,
  order,
  onRequestSort,
  onPageChange,
  onRowsPerPageChange,
  showGatedCommunitiesColumn,
  renderProfileCell,
  renderActions,
  renderCommunitiesCell,
  tableAriaLabel = "Team table"
}: MembersTableProps<TProfile>) {
  const { palette } = useTheme()
  const columnCount = showGatedCommunitiesColumn ? 7 : 6
  const user = useUser()
  return (
    <>
      <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: "auto", boxShadow: "none" }}>
        <Table size="medium" stickyHeader aria-label={tableAriaLabel}>
          <TableHead>
            <TableRow sx={{ "& th": { borderBottomWidth: 2 } }}>
              <SortableTableHeaderCell<CompanyMembersSortBy>
                label="First Name"
                orderBy={orderBy}
                order={order}
                property="FIRST_NAME"
                onRequestSort={onRequestSort}
                leftPadding
              />
              <SortableTableHeaderCell<CompanyMembersSortBy>
                label="Last Name"
                orderBy={orderBy}
                order={order}
                property="LAST_NAME"
                onRequestSort={onRequestSort}
              />
              <SortableTableHeaderCell<CompanyMembersSortBy>
                label="Email"
                orderBy={orderBy}
                order={order}
                property="EMAIL"
                onRequestSort={onRequestSort}
              />
              {showGatedCommunitiesColumn && (
                <TableCell sx={{ maxWidth: 350 }}>
                  <TableHeaderText>Knowledge Bases</TableHeaderText>
                </TableCell>
              )}
              <TableCell sx={{ maxWidth: 350 }}>
                <TableHeaderText>Roles</TableHeaderText>
              </TableCell>
              <SortableTableHeaderCell<CompanyMembersSortBy>
                label="Member Profile"
                orderBy={orderBy}
                order={order}
                property="PROFILE"
                onRequestSort={onRequestSort}
              />
              <TableCell>
                <TableHeaderText>Actions</TableHeaderText>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ py: 4 }}>
                  <Typography color="text.secondary" align="center">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ py: 4 }}>
                  <Typography color="text.secondary" align="center">
                    No members found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member.userId} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell sx={{ pl: { xs: 2, md: 4 } }}>{member.firstName}</TableCell>
                  <TableCell>{member.lastName}</TableCell>
                  <TableCell>{member.email ?? "—"}</TableCell>
                  {showGatedCommunitiesColumn && renderCommunitiesCell && (
                    <TableCell sx={{ maxWidth: 200 }}>{renderCommunitiesCell(member, user.login)}</TableCell>
                  )}
                  <TableCell sx={{ maxWidth: 200 }}>{renderRolesCell(member, palette)}</TableCell>
                  <TableCell>{renderProfileCell(member)}</TableCell>
                  <TableCell>{renderActions(member)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </>
  )
}
